import type { MessageDescriptor } from 'react-intl';

import { getAdyenError } from '@/errorCodes/adyen';
import { getPasswordChangeErrorCodeMessage } from '@/errorCodes/passwordChange';

import type { OrderAppError } from '@/graphql/orderApp/api';
import type {
  AccountError,
  CheckoutError,
  MetadataError,
  TransactionInitializeError,
} from '@/graphql/saleor/api';
import type {
  AdyenError,
  ApiErrorType,
  PaymentError,
  ServiceError,
} from '@/lib/types';

import { getAccountConfirmErrorCodeMessage } from './accountConfirm';
import { getAccountSignInErrorCodeMessage } from './accountSignIn';
import { getAccountSignUpErrorCodeMessage } from './accountSignUp';
import { getAddressErrorCodeMessage } from './address';
import { getAddWatchErrorCodeMessage } from './addWatch';
import { getCembraErrorCodeMessage } from './cembra';
import { getCheckoutErrorCodeMessage } from './checkout';
import type {
  FormattedError,
  IncomingFormattedError,
  OutgoingFormattedError,
} from './common';
import { commonErrorCodeMessages } from './common';
import { getMetadataCodeMessage } from './metadata';
import { getOrderAppErrorCodeMessage } from './orderApp';
import { serviceErrorCodeMessage } from './service';
import { getTransactionErrorCodeMessage } from './transaction';

export const mapApiErrorCode = (
  type: ApiErrorType,
  error: IncomingFormattedError,
  isFinancingAvailable?: boolean,
  phoneNumber?: string,
): OutgoingFormattedError => {
  let mappedMessage: MessageDescriptor | string =
    commonErrorCodeMessages.unexpectedErrorOccurred;
  let errorValues = error.values;

  switch (type) {
    case 'accountConfirm':
      mappedMessage = getAccountConfirmErrorCodeMessage(
        error as FormattedError<AccountError>,
      );
      break;

    case 'accountSignIn':
      mappedMessage = getAccountSignInErrorCodeMessage(
        error as FormattedError<AccountError>,
      );
      break;

    case 'accountSignUp':
      mappedMessage = getAccountSignUpErrorCodeMessage(
        error as FormattedError<AccountError>,
      );
      break;

    case 'orderApp':
      mappedMessage = getOrderAppErrorCodeMessage(
        error as FormattedError<OrderAppError>,
      );
      break;

    case 'checkout':
      mappedMessage = getCheckoutErrorCodeMessage(
        error as FormattedError<CheckoutError | OrderAppError>,
      );
      break;

    case 'metadata':
      mappedMessage = getMetadataCodeMessage(
        error as FormattedError<MetadataError>,
      );
      break;

    case 'transaction':
      mappedMessage = getTransactionErrorCodeMessage(
        error as FormattedError<TransactionInitializeError>,
      );
      break;

    case 'adyen': {
      const { message, values } = getAdyenError(
        error as FormattedError<AdyenError>,
        isFinancingAvailable,
        phoneNumber,
      );
      mappedMessage = message;
      errorValues = values;
      break;
    }

    case 'setPassword':
      mappedMessage = getPasswordChangeErrorCodeMessage(
        error as FormattedError<AccountError>,
      );
      break;

    case 'address':
      mappedMessage = getAddressErrorCodeMessage(
        error as FormattedError<AccountError>,
      );
      break;
    case 'addWatch':
      mappedMessage = getAddWatchErrorCodeMessage(
        error as FormattedError<AccountError>,
      );

      break;
    case 'service':
      mappedMessage = serviceErrorCodeMessage(
        error as FormattedError<ServiceError>,
      );
      break;
    case 'cembra':
      mappedMessage = getCembraErrorCodeMessage(
        error as FormattedError<PaymentError>,
      );
      break;
  }

  return {
    message: mappedMessage,
    values: errorValues,
    code: error.code,
  };
};

export const mapApiErrorCodes = (
  type: ApiErrorType,
  errors: IncomingFormattedError[],
) => errors.map((error) => mapApiErrorCode(type, error));

export type MappedError = ReturnType<typeof mapApiErrorCodes>[number];
export type MappedErrors = MappedError[];
